/**
 * ChatService — Singleton bridging network events, Dexie DB, and UI
 *
 * Listens to incoming chat messages from NetworkManager, persists to local
 * IndexedDB via Dexie, and emits events for UI components to consume.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import type { NetworkManager } from '../network/NetworkManager';
import type { ChatMessagePayload, ChatMessageType } from '@sdl/shared';
import {
  getConversations,
  getMessages,
  saveMessage,
  deleteConversation,
  markConversationRead,
  getUnreadCount,
  type StoredMessage,
  type Conversation,
} from '../db/ChatDatabase';

interface ChatServiceEvents {
  'message:received': StoredMessage;
  'conversations:updated': Conversation[];
  'unread:changed': { count: number };
}

export class ChatService extends EventEmitter<ChatServiceEvents> {
  private networkManager: NetworkManager;
  private localPlayerId: string;
  private _unreadCount = 0;

  constructor(networkManager: NetworkManager, localPlayerId: string) {
    super();
    this.networkManager = networkManager;
    this.localPlayerId = localPlayerId;
    this.setupListeners();
    this.refreshUnreadCount();
  }

  get unreadCount(): number {
    return this._unreadCount;
  }

  /**
   * Send a text/emoji message
   */
  async sendMessage(to: string, toUsername: string, toPicture: string, content: string, type: ChatMessageType = 'text'): Promise<void> {
    const msg: StoredMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      localPlayerId: this.localPlayerId,
      peerId: to,
      from: this.localPlayerId,
      fromUsername: '', // Will be filled by server echo
      content,
      type,
      timestamp: Date.now(),
      read: 1, // Own messages are always read
    };

    // Save locally immediately (optimistic)
    await saveMessage(this.localPlayerId, msg, toUsername, toPicture);
    this.emit('message:received', msg);
    this.notifyConversationsUpdated();

    // Send to server
    this.networkManager.sendChatMessage(to, content, type);
  }

  /**
   * Send an image message (placeholder - CDN upload to be implemented)
   */
  async sendImage(to: string, toUsername: string, toPicture: string, imageUrl: string): Promise<void> {
    const msg: StoredMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      localPlayerId: this.localPlayerId,
      peerId: to,
      from: this.localPlayerId,
      fromUsername: '',
      content: imageUrl,
      type: 'image',
      timestamp: Date.now(),
      read: 1,
    };

    await saveMessage(this.localPlayerId, msg, toUsername, toPicture);
    this.emit('message:received', msg);
    this.notifyConversationsUpdated();

    this.networkManager.sendChatMessage(to, imageUrl, 'image', imageUrl);
  }

  /**
   * Send a game invite message
   */
  async sendInvite(
    to: string,
    toUsername: string,
    toPicture: string,
    inviteData: { gameType: 'ceelo' | 'craps'; stakes: string; roomId?: string },
  ): Promise<void> {
    const content = `Game invite: ${inviteData.gameType.toUpperCase()} (${inviteData.stakes})`;
    const msg: StoredMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      localPlayerId: this.localPlayerId,
      peerId: to,
      from: this.localPlayerId,
      fromUsername: '',
      content,
      type: 'invite',
      timestamp: Date.now(),
      read: 1,
    };

    await saveMessage(this.localPlayerId, msg, toUsername, toPicture);
    this.emit('message:received', msg);
    this.notifyConversationsUpdated();

    this.networkManager.sendChatMessage(to, content, 'invite', undefined, inviteData);
  }

  async getConversations(): Promise<Conversation[]> {
    return getConversations(this.localPlayerId);
  }

  async getMessages(peerId: string, limit?: number, beforeTimestamp?: number): Promise<StoredMessage[]> {
    return getMessages(this.localPlayerId, peerId, limit, beforeTimestamp);
  }

  async deleteConversation(peerId: string): Promise<void> {
    await deleteConversation(this.localPlayerId, peerId);
    this.notifyConversationsUpdated();
  }

  async markRead(peerId: string): Promise<void> {
    await markConversationRead(this.localPlayerId, peerId);
    await this.refreshUnreadCount();
  }

  async refreshUnreadCount(): Promise<void> {
    this._unreadCount = await getUnreadCount(this.localPlayerId);
    this.emit('unread:changed', { count: this._unreadCount });
  }

  dispose(): void {
    this.removeAllListeners();
  }

  private setupListeners(): void {
    // Listen for incoming chat messages from the network
    this.networkManager.on('chat:message' as any, async (data: ChatMessagePayload) => {
      // Skip echo of own messages (server sends back with _echo flag)
      if ((data as any)._echo) return;

      const peerId = data.from;
      const msg: StoredMessage = {
        id: data.id,
        localPlayerId: this.localPlayerId,
        peerId,
        from: data.from,
        fromUsername: data.fromUsername,
        fromPicture: data.fromPicture,
        content: data.content,
        type: data.type,
        timestamp: data.timestamp,
        read: 0, // Incoming messages start as unread
      };

      await saveMessage(this.localPlayerId, msg, data.fromUsername, data.fromPicture ?? '');
      // Refresh the unread count BEFORE emitting so listeners (e.g. the chat
      // bubble) read the fresh count synchronously off `unreadCount` rather than
      // the pre-message value. refreshUnreadCount also emits 'unread:changed'.
      await this.refreshUnreadCount();
      this.emit('message:received', msg);
      this.notifyConversationsUpdated();
    });
  }

  private async notifyConversationsUpdated(): Promise<void> {
    const convos = await this.getConversations();
    this.emit('conversations:updated', convos);
  }
}
